import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';
import type { DataLayerProvenance } from '../shared/data-layer.js';
import { exceedsFreeDataLayerFeatures } from '../shared/drawing-layer.js';

export function oversizedDatasetFeatureCount(layers: Array<{ persisted?: boolean; featureCount: number; provenance?: DataLayerProvenance }>): number | null {
  const oversized = layers.filter(layer => !layer.persisted && exceedsFreeDataLayerFeatures(layer, layer.featureCount));
  return oversized.length ? Math.max(...oversized.map(layer => layer.featureCount)) : null;
}

export type DatasetSaveResume = 'signin' | 'payment';

export function datasetSaveReturnTo(returnTo: string, stage: DatasetSaveResume): string {
  const url = new URL(returnTo, window.location.origin);
  url.searchParams.delete('save_draft');
  url.searchParams.set('resume_save', stage);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function createProDatasetSave(deps: {
  oversizedFeatureCount(): number | null;
  title(): string;
  hasCatalogueDataset(): boolean;
  signedIn(): boolean;
  isPro(): boolean;
  refreshUser(): Promise<void>;
  saveDraft(): Promise<string>;
  signin(options: { next: string; title: string; description: string; datasetSave?: { name: string; featureCount: number } }): void;
  checkout(): Promise<void>;
  save(): Promise<boolean>;
  notify(message: string): void;
  t(key: string): string;
  track: MapAnalyticsTrack;
}) {
  const copy = (key: string) => deps.t(`dataset_save.${key}`);
  async function prepare(): Promise<boolean> {
    const count = deps.oversizedFeatureCount();
    if (deps.signedIn() || (count === null && !deps.hasCatalogueDataset())) return true;
    const props = { feature: 'data_layers', source: 'data_layer_save', user_plan: deps.isPro() ? 'paid_guest' : 'anonymous', coupon_active: 'false' };
    if (count !== null) deps.track('premium_gate_viewed', { ...props, trial: 'false' });
    if (count !== null) deps.track('premium_feature_prompted', props);
    const next = datasetSaveReturnTo(await deps.saveDraft(), 'signin');
    deps.track('save_prompted_signin');
    deps.signin(count === null
      ? { next, title: copy('title').replace('{name}', deps.title()), description: deps.t('signin_dialog.description') }
      : { next, title: copy('headline'), description: copy('next'), datasetSave: { name: deps.title(), featureCount: count } });
    return false;
  }
  let state: { status: 'idle' } | { status: 'checking-account'; stage: DatasetSaveResume }
    | { status: 'saving'; paid: boolean } | { status: 'checkout' } = { status: 'idle' };
  async function resume(stage: DatasetSaveResume): Promise<void> {
    if (state.status !== 'idle') return;
    state = { status: 'checking-account', stage };
    try {
      await deps.refreshUser();
      if (!deps.signedIn()) { deps.notify(copy('signin_cancelled')); return; }
      if (deps.isPro() || deps.oversizedFeatureCount() === null) {
        state = { status: 'saving', paid: deps.isPro() };
        if (await deps.save()) deps.notify(copy('saved'));
      } else if (stage === 'signin') { state = { status: 'checkout' }; await deps.checkout(); }
      else deps.notify(copy('payment_pending'));
    } finally { state = { status: 'idle' }; }
  }
  return { prepare, resume, get saving() { return state.status === 'saving' && state.paid; } };
}
